import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

const textVariants = cva('', {
	variants: {
		/** Typography scale from DESIGN.md: display > title > body > caption > micro; label/data are special-purpose. */
		variant: {
			display: 'font-sans text-2xl font-bold leading-tight tracking-tight',
			title: 'text-[15px] font-bold leading-snug',
			subtitle: 'text-[14px] font-semibold leading-snug',
			body: 'text-[13px] leading-normal',
			caption: 'text-[12px] leading-snug',
			micro: 'text-[11px] leading-tight',
			label: 'text-[12px] font-semibold uppercase tracking-wider',
			data: 'font-mono text-2xl font-extrabold leading-none',
			dataMd: 'font-mono text-base font-extrabold leading-none',
			dataSm: 'font-mono text-sm font-bold leading-none',
			/** No size/weight of its own — inherits from the parent; combine with `tone`/`weight`. */
			inherit: '',
			/** Font dei numeri alla dimensione del genitore — valori inline. */
			monoInline: 'font-mono',
		},
		tone: {
			primary: 'text-text-primary',
			muted: 'text-text-muted',
			accent: 'text-accent',
			purple: 'text-accent-purple',
			success: 'text-accent-green',
			warn: 'text-warn',
			danger: 'text-danger',
			inherit: 'text-inherit',
		},
		weight: {
			normal: 'font-normal',
			medium: 'font-medium',
			semibold: 'font-semibold',
			bold: 'font-bold',
		},
		align: {
			left: 'text-left',
			center: 'text-center',
			right: 'text-right',
		},
		truncate: {
			true: 'truncate',
		},
		/** Preserve newlines in user-authored copy. */
		pre: {
			true: 'whitespace-pre-wrap',
		},
		italic: {
			true: 'italic',
		},
		/** Sizes and aligns an inline leading svg icon. */
		withIcon: {
			true: '[&_svg]:mr-0.5 [&_svg]:inline [&_svg]:h-3 [&_svg]:w-3 [&_svg]:align-[-2px]',
		},
		/** Clamp long copy to N lines. */
		clamp: {
			1: 'line-clamp-1',
			2: 'line-clamp-2',
			3: 'line-clamp-3',
		},
	},
	defaultVariants: {
		variant: 'body',
		tone: 'primary',
	},
});

type TextElement =
	| 'p'
	| 'span'
	| 'div'
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'label'
	| 'strong'
	| 'dt'
	| 'dd';

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
	/** Elemento HTML da renderizzare (p, span, h1-h4, ecc.). Default: `p`. */
	as?: TextElement;
	/** Id del campo associato, quando `as="label"`. */
	htmlFor?: string;
	/** Scala tipografica (display/title/body/caption/micro/label/data, ecc). Default: `body`. */
	variant?: VariantProps<typeof textVariants>['variant'];
	/** Colore del testo. Default: `primary`. */
	tone?: VariantProps<typeof textVariants>['tone'];
	/** Peso del font, per scostarsi dal default della variante. */
	weight?: VariantProps<typeof textVariants>['weight'];
	/** Allineamento orizzontale del testo. */
	align?: VariantProps<typeof textVariants>['align'];
	/** Se true, tronca il testo su una riga con ellissi. */
	truncate?: VariantProps<typeof textVariants>['truncate'];
	/** Limita il testo a N righe (line-clamp), con ellissi. */
	clamp?: VariantProps<typeof textVariants>['clamp'];
	/** Se true, preserva gli a-capo del testo (utile per copy inserita dall'utente). */
	pre?: VariantProps<typeof textVariants>['pre'];
	/** Se true, applica lo stile corsivo. */
	italic?: VariantProps<typeof textVariants>['italic'];
	/** Se true, dimensiona/allinea un'icona SVG inline all'inizio del testo. */
	withIcon?: VariantProps<typeof textVariants>['withIcon'];
}

/** Elemento tipografico generico dell'app: incapsula tutte le scale/colori/pesi del design system in un'unica API. */
const Text = React.forwardRef<HTMLElement, TextProps>(
	(
		{
			className,
			as = 'p',
			variant,
			tone,
			weight,
			align,
			truncate,
			clamp,
			pre,
			italic,
			withIcon,
			...props
		},
		ref,
	) => {
		const Comp = as;
		return (
			<Comp
				ref={ref as React.Ref<never>}
				className={cn(
					textVariants({
						variant,
						tone,
						weight,
						align,
						truncate,
						clamp,
						pre,
						italic,
						withIcon,
						className,
					}),
				)}
				{...props}
			/>
		);
	},
);
Text.displayName = 'Text';

export type HeadingProps = Omit<TextProps, 'as' | 'variant'> & {
	/** Livello del titolo (1-4), determina il tag `h1`-`h4` renderizzato. Default: 1. */
	level?: 1 | 2 | 3 | 4;
	/** Scala tipografica. Default: `title`. */
	variant?: TextProps['variant'];
};

/** Titolo semantico (h1-h4), con la scala `title` come default. */
const Heading = React.forwardRef<HTMLElement, HeadingProps>(
	({ level = 1, variant = 'title', ...props }, ref) => (
		<Text ref={ref} as={`h${level}` as TextElement} variant={variant} {...props} />
	),
);
Heading.displayName = 'Heading';

export { Heading, Text, textVariants };
